import { useState } from "react";
import { MessageSquarePlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

/** Shown when no departure is bookable; sends an on-request enquiry to the team. */
export function OnRequestPanel({ safari, date, bookingType, vehicles, adults, children, testid = "on-request", stack = false,
  title = "Available on request", text = "No jeeps open for this date online. Send a request and our team will try to arrange one." }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ guest_name: "", guest_email: "", guest_mobile: "", notes: "", session: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const sessions = safari?.sessions || [];

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/requests", {
        safari_id: safari.id, date, booking_type: bookingType, vehicles: bookingType === "private" ? vehicles : 0,
        adults, children, ...form,
      });
      setDone(data.request_ref);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className={`w-full rounded-xl border border-dashed border-[#C86D51]/60 bg-[#FBF1EC] p-4 flex flex-col gap-3 ${stack ? "" : "sm:flex-row sm:items-center"}`} data-testid={`${testid}-panel`}>
      <div className="flex-1 text-sm">
        <div className="font-semibold text-[#1C2421]">{title}</div>
        <div className="text-[#5C6B63]">{text}</div>
      </div>
      <button type="button" onClick={() => { setDone(null); setOpen(true); }} className="jt-pill-accent text-sm" data-testid={`${testid}-btn`}>
        <MessageSquarePlus size={15}/> Request this safari
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>{done ? "Request sent" : `Request ${safari?.name}`}</DialogTitle></DialogHeader>
          {done ? (
            <div className="text-center py-4" data-testid={`${testid}-success`}>
              <CheckCircle2 className="mx-auto text-[#1E3A2B] mb-3" size={40}/>
              <p className="text-sm text-[#5C6B63]">We&apos;ll get back to you on WhatsApp or phone shortly. Your reference:</p>
              <p className="font-mono text-xl text-[#1E3A2B] mt-2" data-testid={`${testid}-ref`}>{done}</p>
            </div>
          ) : (
            <div className="grid gap-3">
              <p className="text-xs text-[#5C6B63] -mt-1">{date} · {bookingType === "private" ? `Private jeep × ${vehicles}` : "Shared jeep"} · {adults} adults{children ? `, ${children} children` : ""}</p>
              {sessions.length > 1 && (
                <div className="flex gap-2 flex-wrap">
                  {sessions.map((s) => (
                    <button key={s.time} type="button" onClick={() => setForm({ ...form, session: `${s.label} ${s.time}` })} data-testid={`${testid}-session-${s.time}`}
                      className={`px-3 py-1.5 rounded-full text-sm border ${form.session === `${s.label} ${s.time}` ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "border-[#E2DACF]"}`}>
                      {s.label} · {s.time}
                    </button>
                  ))}
                </div>
              )}
              <Input placeholder="Full name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} data-testid={`${testid}-name`}/>
              <Input placeholder="Email" type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} data-testid={`${testid}-email`}/>
              <Input placeholder="Mobile / WhatsApp" value={form.guest_mobile} onChange={(e) => setForm({ ...form, guest_mobile: e.target.value })} data-testid={`${testid}-mobile`}/>
              <Textarea rows={2} placeholder="Anything we should know? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid={`${testid}-notes`}/>
            </div>
          )}
          <DialogFooter>
            {done ? <button onClick={() => setOpen(false)} className="jt-pill-primary text-sm">Close</button> : (
              <button onClick={submit} disabled={busy || !form.guest_name || !form.guest_email || !form.guest_mobile}
                className="jt-pill-primary text-sm disabled:opacity-40" data-testid={`${testid}-submit`}>{busy ? "Sending…" : "Send request"}</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
